/**
 * Revenue attribution utilities
 * Classifies transactions, enriches metrics with revenue per dimension,
 * and provides formatting helpers.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RevenueData {
  totalRevenue: number
  revenuePerVisitor: number
  conversionRate: number
  conversions: number
}

/** A single transaction row from the backend revenueAttribution array */
export interface RevenueTransaction {
  /** Amount in cents */
  amount_cents?: number
  total_amount_cents?: number
  total_revenue?: number | string
  amount?: number | string
  target?: number | string
  /** Session context fields – present only for attributed transactions */
  session_id?: string
  view_id?: string
  referer_group?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  utm_id?: string
  landing_page?: string
  country?: string
  client_os?: string
  client_browser?: string
  /** Timestamp of the transaction */
  created_at?: string
  date?: string
  /** Provider metadata */
  provider?: string
  stripe_payment_intent_id?: string
  [key: string]: unknown
}

export interface ClassifiedTransactions {
  /** Transactions with at least one session-context field present */
  attributed: RevenueTransaction[]
  /** Transactions with no session-context at all */
  unattributed: RevenueTransaction[]
  /** Totals in cents */
  attributedCents: number
  unattributedCents: number
  totalCents: number
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Safely extract cents from any revenue row shape */
export function toCents(row: RevenueTransaction): number {
  if (row?.amount_cents != null) return Number(row.amount_cents)
  const raw = row?.total_amount_cents ?? row?.total_revenue ?? row?.amount ?? row?.target ?? 0
  const n = Number(raw)
  // Heuristic: if value looks like dollars (< 100_000), convert to cents
  if (n !== 0 && Math.abs(n) < 100_000 && !Number.isInteger(n)) return Math.round(n * 100)
  return Math.round(n)
}

/** Does this transaction have any session context? */
function hasSessionContext(row: RevenueTransaction): boolean {
  return !!(
    row?.session_id ||
    row?.view_id ||
    row?.referer_group ||
    row?.utm_source ||
    row?.utm_medium ||
    row?.utm_campaign ||
    row?.utm_content ||
    row?.utm_term ||
    row?.utm_id
  )
}

// ---------------------------------------------------------------------------
// Transaction classification
// ---------------------------------------------------------------------------

/**
 * Split an array of transactions into attributed (has session context)
 * and unattributed (no session context).
 */
export function classifyTransactions(transactions: RevenueTransaction[]): ClassifiedTransactions {
  const attributed: RevenueTransaction[] = []
  const unattributed: RevenueTransaction[] = []
  let attributedCents = 0
  let unattributedCents = 0

  for (const tx of transactions) {
    const cents = toCents(tx)
    if (hasSessionContext(tx)) {
      attributed.push(tx)
      attributedCents += cents
    } else {
      unattributed.push(tx)
      unattributedCents += cents
    }
  }

  return {
    attributed,
    unattributed,
    attributedCents,
    unattributedCents,
    totalCents: attributedCents + unattributedCents,
  }
}

// ---------------------------------------------------------------------------
// Revenue enrichment per dimension (port from old Svelte frontend)
// ---------------------------------------------------------------------------

function normalizeKey(value: unknown): string {
  if (value === null || value === undefined || value === '') return '__NULL__'
  return String(value).toLowerCase().trim()
}

function normalizeRefererKey(refererGroup: unknown): string {
  if (!refererGroup) return '__NULL__'
  const raw = String(refererGroup).toLowerCase().trim()
  if (raw === 'direct' || raw === '(direct)' || raw === 'none' || raw === '') return 'direct'
  return raw
}

interface RevenueByDimension {
  /** Map of normalized-key → cents */
  bySource: Map<string, number>
  byMedium: Map<string, number>
  byCampaign: Map<string, number>
  byContent: Map<string, number>
  byTerm: Map<string, number>
  byReferrer: Map<string, number>
  byCountry: Map<string, number>
  byLandingPage: Map<string, number>
  byOS: Map<string, number>
  byBrowser: Map<string, number>
}

/**
 * Aggregate attributed revenue by every available dimension.
 * Only considers transactions that have session context.
 */
export function aggregateRevenueByDimension(transactions: RevenueTransaction[]): RevenueByDimension {
  const result: RevenueByDimension = {
    bySource: new Map(),
    byMedium: new Map(),
    byCampaign: new Map(),
    byContent: new Map(),
    byTerm: new Map(),
    byReferrer: new Map(),
    byCountry: new Map(),
    byLandingPage: new Map(),
    byOS: new Map(),
    byBrowser: new Map(),
  }

  const add = (map: Map<string, number>, key: unknown, cents: number) => {
    const k = normalizeKey(key)
    if (k === '__NULL__') return
    map.set(k, (map.get(k) ?? 0) + cents)
  }

  for (const tx of transactions) {
    if (!hasSessionContext(tx)) continue
    const cents = toCents(tx)
    if (!cents) continue

    add(result.bySource, tx.utm_source, cents)
    add(result.byMedium, tx.utm_medium, cents)
    add(result.byCampaign, tx.utm_campaign, cents)
    add(result.byContent, tx.utm_content, cents)
    add(result.byTerm, tx.utm_term, cents)
    add(result.byReferrer, normalizeRefererKey(tx.referer_group), cents)
    add(result.byCountry, tx.country, cents)
    add(result.byLandingPage, tx.landing_page, cents)
    add(result.byOS, tx.client_os, cents)
    add(result.byBrowser, tx.client_browser, cents)
  }

  return result
}

/**
 * Enrich metrics rows (e.g. pages, UTM results, referrals, aggregate) with
 * revenue_direct_cents from the revenueAttribution transaction array.
 * Returns a new metrics object (does not mutate the original).
 */
function enrichMetricsWithRevenue(metrics: any, revenueAttribution: RevenueTransaction[]): any {
  if (!metrics || !Array.isArray(revenueAttribution) || revenueAttribution.length === 0) return metrics

  const dims = aggregateRevenueByDimension(revenueAttribution)

  const mergeDirectRevenue = (row: any, value: number) => ({
    ...row,
    revenue_direct_cents: (Number(row?.revenue_direct_cents) || 0) + value,
  })

  let mutated = false
  const clone: any = { ...metrics }

  // Aggregate rows (referer_group, client_os, client_browser, country)
  if (Array.isArray(metrics.aggregate)) {
    let changed = false
    clone.aggregate = metrics.aggregate.map((row: any) => {
      let cents = 0
      if (row?.referer_group_aggregate === 'referer_group') {
        cents = dims.byReferrer.get(normalizeRefererKey(row?.referer_group)) ?? 0
      } else if (row?.client_os_aggregate === 'client_os') {
        cents = dims.byOS.get(normalizeKey(row?.client_os)) ?? 0
      } else if (row?.client_browser_aggregate === 'client_browser') {
        cents = dims.byBrowser.get(normalizeKey(row?.client_browser)) ?? 0
      } else if (row?.country_aggregate === 'country') {
        cents = dims.byCountry.get(normalizeKey(row?.country)) ?? 0
      }
      if (!cents) return row
      changed = true
      return mergeDirectRevenue(row, cents)
    })
    if (changed) mutated = true
    else clone.aggregate = metrics.aggregate
  }

  // Referrals
  if (Array.isArray(metrics.referrals)) {
    let changed = false
    clone.referrals = metrics.referrals.map((row: any) => {
      const cents = dims.byReferrer.get(normalizeRefererKey(row?.referer_group)) ?? 0
      if (!cents) return row
      changed = true
      return mergeDirectRevenue(row, cents)
    })
    if (!changed) clone.referrals = metrics.referrals
    else mutated = true
  }

  // UTM results
  if (Array.isArray(metrics.utmResults)) {
    let changed = false
    clone.utmResults = metrics.utmResults.map((row: any) => {
      let cents = 0
      if (row?.utm_source) cents = dims.bySource.get(normalizeKey(row.utm_source)) ?? 0
      else if (row?.utm_medium) cents = dims.byMedium.get(normalizeKey(row.utm_medium)) ?? 0
      else if (row?.utm_campaign) cents = dims.byCampaign.get(normalizeKey(row.utm_campaign)) ?? 0
      else if (row?.utm_content) cents = dims.byContent.get(normalizeKey(row.utm_content)) ?? 0
      else if (row?.utm_term) cents = dims.byTerm.get(normalizeKey(row.utm_term)) ?? 0
      if (!cents) return row
      changed = true
      return mergeDirectRevenue(row, cents)
    })
    if (!changed) clone.utmResults = metrics.utmResults
    else mutated = true
  }

  // Entry pages
  if (Array.isArray(metrics.entryPages)) {
    let changed = false
    clone.entryPages = metrics.entryPages.map((row: any) => {
      const cents = dims.byLandingPage.get(normalizeKey(row?.page)) ?? 0
      if (!cents) return row
      changed = true
      return mergeDirectRevenue(row, cents)
    })
    if (!changed) clone.entryPages = metrics.entryPages
    else mutated = true
  }

  // Page sessions
  if (metrics?.pages?.pageSessions && Array.isArray(metrics.pages.pageSessions)) {
    let changed = false
    const pageSessions = metrics.pages.pageSessions.map((row: any) => {
      const cents = dims.byLandingPage.get(normalizeKey(row?.page)) ?? 0
      if (!cents) return row
      changed = true
      return mergeDirectRevenue(row, cents)
    })
    if (changed) {
      clone.pages = { ...(metrics.pages || {}), pageSessions }
      mutated = true
    }
  }

  return mutated ? clone : metrics
}

// ---------------------------------------------------------------------------
// Revenue aggregation by day (for daily charts)
// ---------------------------------------------------------------------------

interface DailyRevenue {
  date: string
  totalCents: number
  attributedCents: number
  unattributedCents: number
  transactionCount: number
  attributedCount: number
}

/**
 * Group revenueAll (aggregated) and revenueAttribution (per-tx) by day.
 * Returns one row per date bucket.
 */
function aggregateRevenueByDay(
  revenueAll: any[],
  revenueAttribution: RevenueTransaction[]
): DailyRevenue[] {
  // Build a map of date → total cents from the aggregated revenue array
  const dayMap = new Map<string, DailyRevenue>()

  // revenueAll rows have time_bucket / granularity and total_revenue (cents)
  for (const row of (revenueAll || [])) {
    const dateKey = (row?.time_bucket || row?.date || '').slice(0, 10)
    if (!dateKey) continue
    const cents = Math.round(Number(row?.total_revenue ?? 0))
    const existing = dayMap.get(dateKey)
    if (existing) {
      existing.totalCents += cents
      existing.transactionCount += Number(row?.transaction_count ?? 1)
    } else {
      dayMap.set(dateKey, {
        date: dateKey,
        totalCents: cents,
        attributedCents: 0,
        unattributedCents: 0,
        transactionCount: Number(row?.transaction_count ?? 1),
        attributedCount: 0,
      })
    }
  }

  // Overlay attributed amounts from per-transaction data
  for (const tx of (revenueAttribution || [])) {
    const dateKey = (tx?.created_at || tx?.date || '').slice(0, 10)
    if (!dateKey) continue
    const cents = toCents(tx)
    const isAttr = hasSessionContext(tx)
    const existing = dayMap.get(dateKey)
    if (existing) {
      if (isAttr) {
        existing.attributedCents += cents
        existing.attributedCount++
      }
    } else {
      dayMap.set(dateKey, {
        date: dateKey,
        totalCents: cents,
        attributedCents: isAttr ? cents : 0,
        unattributedCents: isAttr ? 0 : cents,
        transactionCount: 1,
        attributedCount: isAttr ? 1 : 0,
      })
    }
  }

  // Calculate unattributed as total - attributed
  for (const entry of dayMap.values()) {
    entry.unattributedCents = Math.max(0, entry.totalCents - entry.attributedCents)
  }

  return Array.from(dayMap.values()).sort((a, b) => a.date.localeCompare(b.date))
}

// ---------------------------------------------------------------------------
// Legacy mock generator (kept for backward compatibility)
// ---------------------------------------------------------------------------

function generateMockRevenueData(sessionCount: number, period: string): RevenueData {
  if (!sessionCount || sessionCount === 0) {
    return { totalRevenue: 0, revenuePerVisitor: 0, conversionRate: 0, conversions: 0 }
  }

  const baseConversionRate = 0.025
  const periodMultipliers: Record<string, number> = {
    TODAY: 1.2, YESTERDAY: 1.1, PAST_24_H: 1.15, LAST_7_DAYS: 1.0,
    WEEK_TO_DATE: 0.95, LAST_30_DAYS: 0.9, LAST_MONTH: 0.9, MONTH_TO_DATE: 0.85,
    LAST_90_DAYS: 0.8, QUARTER_TO_DATE: 0.8, LAST_12_MONTHS: 0.75, YEAR_TO_DATE: 0.75, ALL_TIME: 0.7,
  }

  const multiplier = periodMultipliers[period] || 0.85
  const conversionRate = baseConversionRate * multiplier
  const conversions = Math.floor(sessionCount * conversionRate)
  const baseAOV = sessionCount < 100 ? 85 : sessionCount < 1000 ? 65 : 45
  const aovVariation = (Math.random() - 0.5) * 20
  const averageOrderValue = Math.max(25, baseAOV + aovVariation)
  const totalRevenue = conversions * averageOrderValue
  const revenuePerVisitor = totalRevenue / sessionCount

  return {
    totalRevenue: Math.round(totalRevenue * 100) / 100,
    revenuePerVisitor: Math.round(revenuePerVisitor * 100) / 100,
    conversionRate: Math.round(conversionRate * 10000) / 100,
    conversions,
  }
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatCurrency(value: number): string {
  if (value === 0) return '$0'
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`
  if (value >= 1) return `$${value.toFixed(0)}`
  return `$${value.toFixed(2)}`
}

export function formatCurrencyExact(value: number): string {
  if (value === 0) return '$0.00'
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function formatPercentage(value: number): string {
  if (value === 0) return '0%'
  return `${value.toFixed(1)}%`
}

export function privatize(value: string, isPrivate: boolean): string {
  return isPrivate ? '•••' : value
}